import AutoComplete from '@tarekraafat/autocomplete.js';
import { ComponentSelector, ComponentInjector } from '@atypon/ui-utils';
import CoreQuickSearch, {
    QuickSearchElements,
    QuickSearchElm,
    QuickSearchSelectors,
    QuickSearchTexts,
    QuickSearchClassList
} from 'ui-core/widgets/quick-search/js/quick-search';

import { VPortListener } from 'ui-core/plugins/enquireJs/js/helper';

class Texts extends QuickSearchTexts { }

class ClassList extends QuickSearchClassList {
    focus = 'js--focus';
    menuQS = 'menu-qs';
    opened = 'quick-search--opened';
    a11yFocus = 'quick-search__links-wrapper--focused';
    journalSearch = 'quick-search--journal';
}

class Selectors extends QuickSearchSelectors {
    quickLinksWrapper = new ComponentSelector('.quick-search__links-wrapper');
    quickLinks = new ComponentSelector("[class^=quick-search]", 'quickLinksWrapper');
    submit = '.quick-search__button';
    citationSubmit = new ComponentSelector('.citation-search__submit');
    toggler = new ComponentSelector('.quick-search__toggler');
    quickSearchFormContainer = new ComponentSelector('.quickSearchFormContainer');
    journalSearchHideAutocompleteWrapper = '.journal-search__wrapper';
}

class Elements extends QuickSearchElements {
    quickLinksWrapper: HTMLElement;
    quickLinks: HTMLElement[] = [];
    citationSubmit: HTMLElement;
    toggler: HTMLElement;
    quickSearchFormContainer: HTMLElement;
}

interface QuickSearch {
    get elements(): Elements;
    get texts(): Texts;
    get classList(): ClassList;
    get selectors(): Selectors;
}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class QuickSearch extends CoreQuickSearch {

    @VPortListener('md')
    protected isMobile = false;

    protected getConfigs(quickSearchElm: QuickSearchElm, autoCompleteObj: AutoComplete) {
        return {
            data: {
                src: async () => {
                    const url = this.buildFetchURL(quickSearchElm);
                    if (url) {
                        const data = await fetch(url),
                            parsed = await data.json();
                        return parsed.map((item) => {
                            if (item.param === 'SeriesKey') {
                                item.paramFormatted = 'Journal';
                            } else if (item.param === 'ContentGroupKey') {
                                if (item.seriesKey === 'mrwseries') {
                                    item.paramFormatted = 'Reference Work';
                                } else {
                                    item.paramFormatted = 'Book';
                                }
                            }
                            return item;
                        });
                    } else {
                        return [];
                    }
                },
                cache: false
            },
            selector: () => quickSearchElm.el,
            resultsList: this.resultListConfig(quickSearchElm),
            events: {
                input: {
                    blur: (event) => {
                        setTimeout(() => {
                            autoCompleteObj.close(autoCompleteObj.input);
                        }, 200);
                    }
                }
            },
            ...this.defaultConfigs
        };
    }
    protected resultListConfig(quickSearchElm: QuickSearchElm) {
        return { ...super.resultListConfig(quickSearchElm), position: 'afterbegin' };
    }

    protected resultListDestination(quickSearchElm: QuickSearchElm) {
        if (this.domUtils.containsClasses(this.wrapper, this.classList.journalSearch)) {
            return this.wrapper;
        }
        return this.elements.quickLinksWrapper ?? super.resultListDestination(quickSearchElm);
    }

    protected get hasMenu() {
        return this.domUtils.containsClasses(this.wrapper, this.classList.menuQS);
    }

    protected getCurrentAutoComplete(field: HTMLElement): [QuickSearchElm, any] {
        return Array.from(this.autcompleteInstances).find(item => item[0].el === field);
    }

    addEventListeners(): void {
        super.addEventListeners();
        this.registerAutoCompleteInputEvents();
        this.registerCitationSearchEvents();
        this.registerTogglerOverlayEvents();

        if (this.elements.quickLinks.length > 0) {
            this.checkCitationOnPageLoad();
        }
    }

    protected checkCitationOnPageLoad() {
        window.addEventListener("hashchange", () => {
            if (location.hash === "#citation") {
                window.location.reload();
            }
        });
    }

    protected registerAutoCompleteInputEvents() {
        this.registerAutoCompleteFocus();
        this.domUtils.addEventListener(this.elements.qsAutocomplete, 'keydown', this.onAutocompleteInputKeydown.bind(this))
    }

    protected registerTogglerOverlayEvents() {
        this.domUtils.addEventListener(this.elements.toggler, 'click', this.onToggleClick.bind(this));
        this.domUtils.addEventListener(document.body, 'click', this.onBodyClick.bind(this));
    }

    protected onToggleClick() {
        this.domUtils.toggleClasses(document.body, this.classList.opened);
    }

    protected onBodyClick(e) {
        const target = e.target as HTMLElement;
        const body = document.body;
        if (
            this.domUtils.containsClasses(body, this.classList.opened) &&
            !(target === this.elements.toggler) &&
            !this.domUtils.closest(this.selectors.toggler.selector, e.target) &&
            !(target === this.elements.quickSearchFormContainer) &&
            !this.domUtils.closest(this.selectors.quickSearchFormContainer.selector, e.target)
        ) {
            this.domUtils.toggleClasses(document.body, this.classList.opened, false);
        }
    }

    protected registerAutoCompleteFocus() {
        if (!this.hasMenu) {
            return;
        }
        this.domUtils.addEventListener(this.elements.qsAutocomplete, 'focus', this.onAutoCompleteInputFocusChange.bind(this, true));
        this.domUtils.addEventListener(this.elements.qsAutocomplete, 'focusout', this.onAutoCompleteInputFocusChange.bind(this, false));
        this.domUtils.addEventListener(this.elements.quickLinks, 'focusout', this.onQuickLinksFocusChange.bind(this))
    }

    protected onAutoCompleteInputFocusChange(focused: boolean, event: FocusEvent) {
        if (this.isMobile) {
            return;
        }
        if (this.domUtils.containsClasses(this.elements.quickLinksWrapper, this.classList.a11yFocus)) {
            return;
        }
        this.toggleFocusClasses(focused);
    }

    protected toggleFocusClasses(show: boolean) {
        this.domUtils.toggleClasses(this.elements.qsAutocomplete, this.classList.focus, show);
        this.domUtils.toggleClasses(document.body, this.classList.opened, show);
        setTimeout(()=>{
            if (this.elements.quickLinksWrapper) {
                this.domUtils.animate(show ? 'slideIn' : 'slideOut', this.elements.quickLinksWrapper);
            }
        }, 200)
    }


    protected onAutocompleteInputKeydown(event: KeyboardEvent) {
        if (!this.elements.quickLinksWrapper || event.shiftKey || event.key !== 'Tab') {
            this.removeA11yClasses();
            return;
        }
        event.preventDefault();
        this.domUtils.addClasses(this.elements.quickLinksWrapper, this.classList.a11yFocus);
        const autoComplete = this.getCurrentAutoComplete(event.target as HTMLElement);
        const link = this.elements.quickLinks[0];
        if (autoComplete && autoComplete[1].isOpen && autoComplete[1].cursor !== autoComplete[1].feedback.results.length - 1) {
            autoComplete[1].next();
        } else {
            link?.focus();
        }
    }

    protected onQuickLinksFocusChange(event: FocusEvent) {
        if (this.isMobile) {
            return;
        }
        const target = event.target as HTMLElement;
        const children = target.parentElement.children;
        if (target !== children[children.length - 1]) {
            return;
        }
        const parent = this.domUtils.closest(this.selectors.qsItems, target);
        if (!parent) {
            return;
        }
        this.domUtils.getElement(this.selectors.submit, parent)?.focus();
        this.removeA11yClasses();
        this.toggleFocusClasses(false);
    }

    protected removeA11yClasses() {
        this.domUtils.removeClasses(this.elements.quickLinksWrapper, this.classList.a11yFocus);
    }

}

export default QuickSearch;

export {
    Elements as PhotoQuickSearchElements,
    Selectors as PhotoQuickSearchSelectors,
    ClassList as PhotoQuickSearchClassList,
    Texts as PhotoQuickSearchTexts
}